import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, XIcon } from 'lucide-react';
import { notifications, Notification } from '../data/content';
import { useIsMobile } from '../hooks/useIsMobile';
const CONFIG = {
  desktop: {
    initialDelay: 8000,
    visible: 4000,
    interval: 6000
  },
  mobile: {
    initialDelay: 10000,
    visible: 3500,
    interval: 8000
  }
};
function pickNext(current: number) {
  if (notifications.length <= 1) return 0;
  let next = current;
  while (next === current) next = Math.floor(Math.random() * notifications.length);
  return next;
}
export function SocialProofPopup() {
  const isMobile = useIsMobile();
  const [active, setActive] = useState<Notification | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const lastIndex = useRef(-1);
  const timers = useRef<number[]>([]);
  const config = isMobile ? CONFIG.mobile : CONFIG.desktop;
  const clearTimers = useCallback(() => {
    timers.current.forEach(timer => window.clearTimeout(timer));
    timers.current = [];
  }, []);
  useEffect(() => {
    if (dismissed || !notifications.length) {
      clearTimers();
      setActive(null);
      return;
    }
    const showNext = () => {
      const element = document.activeElement;
      const typing = element instanceof HTMLElement && (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA' || element.isContentEditable);
      if (typing) {
        timers.current.push(window.setTimeout(showNext, 2000));
        return;
      }
      const next = pickNext(lastIndex.current);
      lastIndex.current = next;
      setActive(notifications[next]);
      timers.current.push(window.setTimeout(() => {
        setActive(null);
        timers.current.push(window.setTimeout(showNext, config.interval));
      }, config.visible));
    };
    timers.current.push(window.setTimeout(showNext, config.initialDelay));
    return clearTimers;
  }, [clearTimers, config.initialDelay, config.interval, config.visible, dismissed]);
  return <div className="pointer-events-none fixed bottom-4 left-3 z-50 sm:bottom-5 sm:left-5" data-id="element-160" data-mp="1a23e5kdyq6va"><AnimatePresence data-id="element-161" data-mp="1h4s0bcigm8vq">{active && !dismissed && <motion.div key={`${active.name}-${active.time}`} initial={{
        x: '-120%',
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        opacity: 0,
        x: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} role="status" aria-live="polite" className="pointer-events-auto w-[220px] max-w-[58vw] rounded-lg border-l-[3px] border-vault-gold bg-deep-navy px-3.5 py-2.5 shadow-[0_8px_24px_-8px_rgba(0,0,0,0.5)] sm:w-[280px] sm:max-w-none" data-id="element-162" data-mp="1h637lmb45yrl"><button onClick={() => setDismissed(true)} aria-label="Close notification" className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full text-white/40 hover:text-white" data-id="element-163" data-mp="20ukcno8s5a2a"><XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-164" data-mp="29r338ttjglvp" /></button><div className="flex items-center gap-1.5 pr-4" data-id="element-165" data-mp="2er7kim29tkmz"><CheckCircle2Icon className="h-3.5 w-3.5 shrink-0 text-vault-gold" aria-hidden="true" data-id="element-166" data-mp="2epwhwpkkaixg" /><span className="text-[11px] font-bold text-white" data-id="element-167" data-mp="fgzigg5pm08n">Verified purchase</span></div><p className="mt-1 text-[11px] leading-snug text-white/80" data-id="element-168" data-mp="kf114zz83xjl">{active.name} — {active.location}</p><p className="mt-0.5 text-[10px] font-medium text-vault-gold" data-id="element-169" data-mp="cvi7lzscslwr">{active.time}</p><p className="mt-0.5 text-[10px] text-white/55" data-id="element-170" data-mp="83qebhhtc22x">The ESL Survival Vault™</p></motion.div>}</AnimatePresence></div>;
}